/* eslint-disable consistent-return */
import { apiPaths, getApiPath, httpStatus } from '../../constants'
import { Api } from '../../middlewares'
import { Logger, LoggerTypes } from '../../utils'

// -- CREATE
export const CREATE_FLOW_TASK_INTERACTION_CHECK_SUCCESS = 'CREATE_FLOW_TASK_INTERACTION_CHECK_SUCCESS'
export const CREATE_FLOW_TASK_INTERACTION_CHECK_FAILURE = 'CREATE_FLOW_TASK_INTERACTION_CHECK_FAILURE'

// -- UPDATE
export const UPDATE_FLOW_TASK_INTERACTION_CHECK_SUCCESS = 'UPDATE_FLOW_TASK_INTERACTION_CHECK_SUCCESS'
export const UPDATE_FLOW_TASK_INTERACTION_CHECK_FAILURE = 'UPDATE_FLOW_TASK_INTERACTION_CHECK_FAILURE'

// -- DELETE
export const DELETE_FLOW_TASK_INTERACTION_CHECK_SUCCESS = 'DELETE_FLOW_TASK_INTERACTION_CHECK_SUCCESS'
export const DELETE_FLOW_TASK_INTERACTION_CHECK_FAILURE = 'DELETE_FLOW_TASK_INTERACTION_CHECK_FAILURE'

// -- FLOW_TASK_INTERACTION CACHE
export const CACHE_CREATE_FLOW_TASK_INTERACTION_CHECK = 'CACHE_CREATE_FLOW_TASK_INTERACTION_CHECK'
export const CACHE_DELETE_FLOW_TASK_INTERACTION_CHECK = 'CACHE_DELETE_FLOW_TASK_INTERACTION_CHECK'

// ____________________________________________________________

/**
 * Create a flowTaskInteraction check broadcast two types of events:
 * - CREATE_FLOW_TASK_INTERACTION_CHECK_SUCCESS: in case of success.
 * - CREATE_FLOW_TASK_INTERACTION_CHECK_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project Id UUID
 * @param {uuid} sid Scenario Id UUID
 * @param {uuid} fid FlowGroup Id UUID
 * @param {uuid} tid Flow task interaction Id UUID
 * @param {object} flowCheck Flow task interaction check to create
 * @returns {Promise<object>}
 */
export const createFlowTaskInteractionCheck = (pid, sid, fid, tid, flowCheck) => async (dispatch) => {
  if (!flowCheck) {
    return
  }

  const path = getApiPath(apiPaths.FLOW_TASK_INTERACTION_CHECK_CREATE, { pid, sid, fid, tid })
  const api = new Api(path)
  const result = await api.create(flowCheck)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowTaskInteractionChecksActions.createFlowTaskInteractionCheck()', {
    path,
    result,
    flowCheck
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: CREATE_FLOW_TASK_INTERACTION_CHECK_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: CREATE_FLOW_TASK_INTERACTION_CHECK_FAILURE,
    data: result.data
  })
}

/**
 * Update the values of a flowTaskInteraction check; broadcast two types of events:
 * - UPDATE_FLOW_TASK_INTERACTION_CHECK_SUCCESS: in case of success.
 * - UPDATE_FLOW_TASK_INTERACTION_CHECK_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project Id UUID
 * @param {uuid} sid Scenario Id UUID
 * @param {uuid} fid FlowGroup Id UUID
 * @param {uuid} tid Flow task interaction Id UUID
 * @param {object} interactionCheck Flow task interaction to update (must contain `id` UUID attribute)
 * @returns {Promise<object>}
 */
export const updateFlowTaskInteractionCheck = (pid, sid, fid, tid, interactionCheck) => async (dispatch) => {
  if (!interactionCheck) {
    return
  }

  const path = getApiPath(apiPaths.FLOW_TASK_INTERACTION_CHECK_UPDATE, { pid, sid, fid, tid, kid: interactionCheck.id })
  const api = new Api(path)
  const result = await api.update(interactionCheck)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowTaskInteractionChecksActions.updateFlowTaskInteractionCheck()', {
    path,
    result,
    interactionCheck
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: UPDATE_FLOW_TASK_INTERACTION_CHECK_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: UPDATE_FLOW_TASK_INTERACTION_CHECK_FAILURE,
    data: result.data
  })
}

/**
 * Delete a flowTaskInteraction check, broadcast two types of events:
 * - DELETE_FLOW_TASK_INTERACTION_CHECK_SUCCESS: in case of success.
 * - DELETE_FLOW_TASK_INTERACTION_CHECK_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project Id UUID
 * @param {uuid} sid Scenario Id UUID
 * @param {uuid} fid FlowGroup Id UUID
 * @param {uuid} tid TaskInteraction Id UUID
 * @param {uuid} kid check Id UUID
 * @returns {Promise<object>}
 */
export const deleteFlowTaskInteractionCheck = (pid, sid, fid, tid, kid) => async (dispatch) => {
  const path = getApiPath(apiPaths.FLOW_TASK_INTERACTION_CHECK_DELETE, { pid, sid, fid, tid, kid })
  const api = new Api(path)
  const result = await api.delete()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] flowTaskInteractionChecksActions.deleteFlowTaskInteractionCheck()', {
    path,
    result
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: DELETE_FLOW_TASK_INTERACTION_CHECK_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: DELETE_FLOW_TASK_INTERACTION_CHECK_FAILURE,
    data: result.data
  })
}

/**
 * Add the definition of a task instance check in the flowGroup's cache.
 *
 * @param {object} flowGroup the flow group of the task
 * @param {object} tid the identifier of the task instance to be updated
 * @param {object} check the check to be added
 * @returns {object}
 */
export const cacheCreateFlowTaskInteractionCheck = (flowGroup, tid, check) => ({
  type: CACHE_CREATE_FLOW_TASK_INTERACTION_CHECK,
  data: {
    flowGroup,
    tid,
    check
  }
})

/**
 * Remove the definition of a task instance check in the flowGroup's cache.
 *
 * @param {object} flowGroup the flow group of the task
 * @param {object} tid the identifier of the task instance to be updated
 * @param {object} checkId the identifier of the check to be removed
 * @returns {object}
 */
export const cacheDeleteFlowTaskInteractionCheck = (flowGroup, tid, checkId) => ({
  type: CACHE_DELETE_FLOW_TASK_INTERACTION_CHECK,
  data: {
    flowGroup,
    tid,
    checkId
  }
})
